import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AccountShell } from "@/components/account/AccountShell";
import { myQuotesQuery } from "@/lib/corporate/queries";
import { decideQuoteFn } from "@/lib/corporate/corporate.functions";
import { formatDate, formatDateTime, formatPaise } from "@/lib/corporate/format";
import { QUOTE_STATUS_LABEL, type CorporateQuote } from "@/lib/corporate/types";

export const Route = createFileRoute("/account/quotes")({
  head: () => ({
    meta: [
      { title: "My Corporate Quotes — CB16 Atelier" },
      { name: "description", content: "Review, accept or decline your CB16 corporate quotes and track them through to order." },
      { property: "og:title", content: "My Corporate Quotes — CB16 Atelier" },
      { property: "og:description", content: "Track your CB16 corporate quotes from request to order." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: QuotesPage,
});

const TONE: Record<string, string> = {
  requested: "bg-black/5 text-black/70",
  in_review: "bg-amber-50 text-amber-800",
  quoted: "bg-[#C6A15B]/15 text-[#8a6d3b]",
  approved: "bg-emerald-50 text-emerald-800",
  rejected: "bg-red-50 text-red-700",
  converted: "bg-emerald-600/10 text-emerald-800",
};

function QuotesPage() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery(myQuotesQuery());
  const [openId, setOpenId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const decide = useMutation({
    mutationFn: (v: { quoteId: string; decision: "approved" | "rejected" }) => decideQuoteFn({ data: v }),
    onSuccess: (res) => {
      if (res.error) setActionError(res.error);
      else {
        setActionError(null);
        void qc.invalidateQueries({ queryKey: ["corporate", "my-quotes"] });
      }
    },
    onError: (e: Error) => setActionError(e.message),
  });

  const quotes = data?.quotes ?? [];

  return (
    <AccountShell>
      <header className="mb-8">
        <h2 className="text-2xl" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500 }}>Corporate quotes</h2>
        <p className="mt-2 text-sm text-black/55">
          Accept a quote to have our team raise the corresponding order.{" "}
          <Link to="/corporate" className="text-[#C6A15B]">Request a new quote</Link>
        </p>
      </header>

      {isLoading && <p className="text-sm text-black/55">Loading your quotes…</p>}
      {error && <p className="text-sm text-red-700">{(error as Error).message}</p>}
      {actionError && <p className="mb-4 text-sm text-red-700">{actionError}</p>}
      {!isLoading && !error && quotes.length === 0 && (
        <p className="text-sm text-black/55">You have no corporate quotes yet.</p>
      )}

      <div className="space-y-4">
        {quotes.map((q) => (
          <article key={q.id} className="border border-black/10 bg-white">
            <div className="flex flex-wrap items-start justify-between gap-4 p-5">
              <div>
                <div className="flex items-center gap-3">
                  <h3 className="text-lg font-medium">{q.quoteNumber}</h3>
                  <span className={`px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.18em] ${TONE[q.status] ?? "bg-black/5"}`}>
                    {QUOTE_STATUS_LABEL[q.status]}
                  </span>
                </div>
                <p className="mt-1 text-sm text-black/60">{q.companyName} · requested {formatDate(q.createdAt)}</p>
                {q.validUntil && q.status === "quoted" && (
                  <p className="mt-1 text-xs text-black/50">Valid until {formatDate(q.validUntil)}</p>
                )}
              </div>
              <div className="text-right">
                <p className="text-xl font-medium">{formatPaise(q.totalCents)}</p>
                <button
                  type="button"
                  className="mt-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#C6A15B]"
                  onClick={() => setOpenId(openId === q.id ? null : q.id)}
                >
                  {openId === q.id ? "Hide details" : "View details"}
                </button>
              </div>
            </div>

            {openId === q.id && <QuoteDetail quote={q} onDecide={(d) => decide.mutate({ quoteId: q.id, decision: d })} busy={decide.isPending} />}
          </article>
        ))}
      </div>
    </AccountShell>
  );
}

function QuoteDetail({
  quote,
  onDecide,
  busy,
}: {
  quote: CorporateQuote;
  onDecide: (d: "approved" | "rejected") => void;
  busy: boolean;
}) {
  const printQuote = () => window.print();
  return (
    <div className="border-t border-black/10 bg-[#faf9f7] p-5">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[520px] text-left text-sm">
          <thead className="text-[10px] uppercase tracking-[0.18em] text-black/45">
            <tr><th className="py-2">Pack</th><th className="py-2">Qty</th><th className="py-2">Unit</th><th className="py-2 text-right">Total</th></tr>
          </thead>
          <tbody>
            {quote.items.map((i) => (
              <tr key={i.id} className="border-t border-black/5">
                <td className="py-2">{i.packName}<span className="block text-xs text-black/45">{i.variantLabel}{i.tierLabel ? ` · ${i.tierLabel}` : ""}</span></td>
                <td className="py-2">{i.qty}</td>
                <td className="py-2">{formatPaise(i.unitPriceCents)}</td>
                <td className="py-2 text-right">{formatPaise(i.lineTotalCents)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <dl className="mt-4 ml-auto max-w-xs space-y-1 text-sm">
        <Row label="Subtotal" value={formatPaise(quote.subtotalCents)} />
        {quote.discountCents > 0 && <Row label="Discount" value={`−${formatPaise(quote.discountCents)}`} />}
        {quote.shippingCents > 0 && <Row label="Shipping" value={formatPaise(quote.shippingCents)} />}
        {quote.taxCents > 0 && <Row label="GST" value={formatPaise(quote.taxCents)} />}
        <div className="flex justify-between border-t border-black/10 pt-2 text-base font-semibold"><dt>Total</dt><dd>{formatPaise(quote.totalCents)}</dd></div>
      </dl>

      {quote.shippingAddresses.length > 0 && (
        <div className="mt-6">
          <h4 className="text-[10px] font-semibold uppercase tracking-[0.2em] text-black/45">Delivery addresses</h4>
          <ul className="mt-2 grid gap-2 sm:grid-cols-2">
            {quote.shippingAddresses.map((s, idx) => (
              <li key={idx} className="border border-black/10 bg-white p-3 text-xs text-black/65">
                <strong className="block text-black">{s.label || s.recipientName}</strong>
                {s.address.line1}, {s.address.city} {s.address.postalCode}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-6">
        <h4 className="text-[10px] font-semibold uppercase tracking-[0.2em] text-black/45">Timeline</h4>
        <ol className="mt-2 space-y-2">
          {quote.events.map((e) => (
            <li key={e.id} className="border-l-2 border-[#C6A15B]/50 pl-3 text-xs text-black/60">
              <span className="font-medium text-black">{e.type}</span> · {formatDateTime(e.createdAt)}
              {e.message && <span className="block">{e.message}</span>}
            </li>
          ))}
          {quote.events.length === 0 && <li className="text-xs text-black/45">No activity recorded yet.</li>}
        </ol>
      </div>

      <div className="mt-6 flex flex-wrap gap-3">
        <button type="button" onClick={printQuote} className="border border-black/25 px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.2em] hover:border-black">
          Download / print
        </button>
        {quote.status === "quoted" && (
          <>
            <button type="button" disabled={busy} onClick={() => onDecide("approved")} className="border border-black bg-black px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-white disabled:opacity-50">
              Accept quote
            </button>
            <button type="button" disabled={busy} onClick={() => onDecide("rejected")} className="border border-red-300 px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-red-700 disabled:opacity-50">
              Decline
            </button>
          </>
        )}
        {quote.status === "approved" && (
          <p className="self-center text-xs text-black/55">Accepted — our team is raising your corporate order.</p>
        )}
        {quote.status === "converted" && (
          <Link to="/account/orders" className="self-center text-[11px] font-semibold uppercase tracking-[0.2em] text-[#C6A15B]">
            View order →
          </Link>
        )}
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between"><dt className="text-black/55">{label}</dt><dd>{value}</dd></div>
  );
}